// EditSelectionsModal/index.tsx
import React from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ServiceSelection } from '../types/planningTypes';
import { useSelectionsForm } from './hooks/useSelectionsForm';
import { useAutofillFromHistory } from './hooks/useAutofillFromHistory';
import { DragDropList } from './components/DragDropList/DragDropList';
import { AutofillDropdown } from './components/AutofillDropdown';
import { sortByLiturgy, buildMissingScaffoldRows } from './utils/liturgicalOrder';
import styles from './styles/index.module.css';

interface EditSelectionsModalProps {
  show: boolean;
  onHide: () => void;
  serviceId: number;
  liturgyName?: string;
  churchId?: number;
  selections: ServiceSelection[];
  onUpdate: (updatedSelections: ServiceSelection[]) => void;
}

const EditSelectionsModal: React.FC<EditSelectionsModalProps> = ({
  show,
  onHide,
  serviceId,
  liturgyName,
  churchId,
  selections,
  onUpdate
}) => {
  const {
    currentSelections,
    isSubmitting,
    handleSelectionChange,
    handleAddSelection,
    handleDeleteSelection,
    handleAppendSelections,
    handleUpdate
  } = useSelectionsForm({
    initialSelections: selections,
    serviceId,
    onUpdateComplete: onUpdate
  });

  // Autofill from history hook
  const {
    availableYears,
    isLoading: isLoadingHistory,
    applyYearSelections
  } = useAutofillFromHistory({
    liturgyName: liturgyName || '',
    churchId,
    serviceId,
    currentSelections,
    onAppendSelections: handleAppendSelections
  });

  const handleSubmit = async () => {
    try {
      const updatedSelections = await handleUpdate();
      if (updatedSelections) {
        onUpdate(updatedSelections);
        onHide();
      }
    } catch (error) {
      console.error('Failed to update selections:', error);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" className={styles.modal}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Service Selections</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <DndProvider backend={HTML5Backend}>
          <DragDropList
            selections={currentSelections}
            onChange={handleSelectionChange}
            onDelete={handleDeleteSelection}
          />
          <div className={styles.actionButtons}>
            <Button
              variant="outline-primary"
              onClick={handleAddSelection}
            >
              Add Song
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => handleSelectionChange(sortByLiturgy(currentSelections))}
              disabled={isSubmitting || currentSelections.length < 2}
              title="Sort by canonical liturgical order; ties keep their current order"
            >
              Sort by liturgy
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                const missing = buildMissingScaffoldRows(serviceId, currentSelections);
                if (missing.length === 0) return;
                handleSelectionChange(sortByLiturgy([...currentSelections, ...missing]));
              }}
              disabled={isSubmitting}
              title="Add blank rows for Opening, Offertory Anthem, Offertory Hymn, 2x Communion, Closing (only slots that are missing)"
            >
              Scaffold hymns
            </Button>
            {liturgyName && (
              <AutofillDropdown
                availableYears={availableYears}
                isLoading={isLoadingHistory}
                onSelectYear={applyYearSelections}
                disabled={isSubmitting}
              />
            )}
          </div>
        </DndProvider>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Updating...
            </>
          ) : (
            'Update Selections'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSelectionsModal;
